/**
 * API Client pro komunikaci s API Workerem
 */

const API_BASE = import.meta.env.VITE_API_URL || '/api';

class ApiClient {
  async request(endpoint, options = {}) {
    const url = `${API_BASE}${endpoint}`;

    // Vytvořit AbortController s delším timeoutem pro generování
    const controller = new AbortController();
    const timeout = options.timeout || (endpoint.includes('/generate') ? 120000 : 30000); // 2 min pro generate, 30s pro ostatní

    const timeoutId = setTimeout(() => controller.abort(), timeout);

    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      signal: controller.signal,
      ...options
    };

    try {
      const response = await fetch(url, config);
      clearTimeout(timeoutId);

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Request failed' }));
        console.error('API Error Details:', error);
        throw new Error(error.error || error.details || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);

      if (error.name === 'AbortError') {
        throw new Error(`Request timeout - generování trvá příliš dlouho (>${timeout/1000}s)`);
      }

      console.error('API Error:', error);
      throw error;
    }
  }

  // Sources
  async getSources() {
    return this.request('/api/sources');
  }

  async getSource(id) {
    return this.request(`/api/sources/${id}`);
  }

  async createSource(data) {
    return this.request('/api/sources', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateSource(id, data) {
    return this.request(`/api/sources/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteSource(id) {
    return this.request(`/api/sources/${id}`, {
      method: 'DELETE'
    });
  }

  // Emails
  async getEmails(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/emails?${query}`);
  }

  async getEmail(id) {
    return this.request(`/api/emails/${id}`);
  }

  async markEmailAnalyzed(id, newsletterId) {
    return this.request(`/api/emails/${id}/mark-analyzed`, {
      method: 'POST',
      body: JSON.stringify({ newsletterId })
    });
  }

  async deleteEmail(id) {
    return this.request(`/api/emails/${id}`, {
      method: 'DELETE'
    });
  }

  async getEmailStats() {
    return this.request('/api/emails/stats/summary');
  }

  // Automation
  async getAutomationConfig() {
    return this.request('/api/automation');
  }

  async updateAutomationConfig(config) {
    return this.request('/api/automation', {
      method: 'PUT',
      body: JSON.stringify(config)
    });
  }

  async testAutomation() {
    return this.request('/api/automation/test', {
      method: 'POST'
    });
  }

  // Newsletters
  async getNewsletters(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/api/newsletters?${query}`);
  }

  async getNewsletter(id) {
    return this.request(`/api/newsletters/${id}`);
  }

  async generateNewsletter(options = {}) {
    return this.request('/api/generate', {
      method: 'POST',
      body: JSON.stringify(options)
    });
  }

  async updateNewsletter(id, data) {
    return this.request(`/api/newsletters/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  // Beehiiv
  async publishToBeehiiv(newsletterId) {
    return this.request(`/api/beehiiv/publish/${newsletterId}`, {
      method: 'POST'
    });
  }

  async getBeehiivStatus(newsletterId) {
    return this.request(`/api/beehiiv/status/${newsletterId}`);
  }
}

export const api = new ApiClient();
